"use client";

import {
  AnimatePresence,
  motion,
  useScroll,
  useMotionValueEvent,
} from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { useState, useEffect } from "react";
import { MdClose } from "react-icons/md";
import { SlMenu } from "react-icons/sl";

import { menuItems, navItems } from "../../data/navigationData";
import GradientBorder from "./GradientBorder";

interface FloatingNavProps {
  visible?: boolean;
}

export default function FloatingNav({ visible = true }: FloatingNavProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { scrollY } = useScroll();

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useMotionValueEvent(scrollY, "change", (latest) => {
    if (!isMobile) {
      setIsHidden(false);
      return;
    }
    const previous = scrollY.getPrevious() ?? 0;
    // Hide when scrolling down, show when scrolling up
    if (latest > previous && latest > 50) {
      setIsHidden(true);
    } else {
      setIsHidden(false);
    }
  });

  const handleMenuClose = () => {
    setIsOpen(false);
  };

  return (
    <>
      {/* Mobile Menu Modal - Appears in place of floating nav */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="fixed bottom-2 left-1/2 z-60 w-[calc(100%-40px)] max-w-[calc(100%-40px)] -translate-x-1/2 md:w-[500px] md:max-w-[400px]"
          >
            <div className="relative h-full w-full rounded-4xl shadow-2xl">
              <div className="flex h-full w-full flex-col overflow-hidden rounded-4xl bg-black/30 backdrop-blur-xl">
                {/* Modal Content container */}
                <div className="relative z-10 flex flex-1 flex-col gap-5 p-5">
                  <div className="flex flex-1">
                    {/* Navigation Links - Centered */}
                    <nav className="flex flex-1 flex-col items-center justify-center gap-6 text-center">
                      {menuItems.map((item) => (
                        <a
                          key={item.name}
                          href={item.href}
                          className="font-raleway text-lg font-medium text-white/60 transition-colors hover:text-white md:text-xl"
                        >
                          {item.name}
                        </a>
                      ))}
                    </nav>
                  </div>

                  {/* Footer: Logo & Close */}
                  <div className="flex items-center justify-center gap-5 pt-2.5">
                    <Link href="/" className="cursor-pointer">
                      <Image
                        src="/logo.webp"
                        alt="Halcyon Amusement Logo"
                        width={78}
                        height={40}
                        className="h-10 w-auto object-contain"
                      />
                    </Link>
                    <span className="font-raleway text-lg font-semibold text-white">
                      Halcyon Amusement
                    </span>

                    <button
                      onClick={handleMenuClose}
                      className="flex h-10 w-10 items-center justify-center rounded-full border border-white text-white transition-all hover:bg-white hover:text-black"
                    >
                      <MdClose className="text-xl" />
                    </button>
                  </div>
                </div>
              </div>
              <GradientBorder rounded="4xl" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Navbar - Hidden when menu is open */}
      <AnimatePresence>
        {!isOpen && visible && !isHidden && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="fixed bottom-2 left-1/2 z-50 w-[calc(100%-40px)] max-w-[calc(100%-40px)] -translate-x-1/2 md:w-auto md:max-w-fit"
          >
            <div className="relative rounded-full shadow-2xl">
              <div className="relative flex items-center gap-5 rounded-full bg-black/30 px-2.5 py-2.5 backdrop-blur-2xl">
                <Link href="/" className="cursor-pointer">
                  <Image
                    src="/logo.webp"
                    alt="Halcyon Amusement Logo"
                    width={63}
                    height={32}
                    className="h-8 w-auto object-contain"
                  />
                </Link>

                <nav className="relative z-10 flex flex-1 items-center justify-center gap-5">
                  {navItems.map((item) => (
                    <a
                      key={item.name}
                      href={item.href}
                      className="font-raleway text-sm font-semibold text-white transition-colors hover:font-bold md:text-base"
                    >
                      {item.name}
                    </a>
                  ))}
                </nav>

                <button
                  onClick={() => setIsOpen(true)}
                  className="relative z-10 flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full border border-white/20 text-white transition-colors hover:bg-white/10"
                >
                  <SlMenu size={20} />
                </button>
              </div>
              <GradientBorder rounded="full" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
